import React from 'react';
import PropTypes from 'prop-types';
import IBAN from 'iban';
import dayjs from 'dayjs';
import { Form, reduxForm, getFormValues } from 'redux-form';

import { connectTo } from '../../../utils';

import {
  mobileNumber as mobileValidator,
  email as emailValidator,
} from '../../../utils/validation';
import phoneTransformer from '../../../utils/phone';

import { Select, Button, Checkbox, TextField, Paper, Translation } from '..';

import {
  getBalanceWithCurrency,
  INSTALLMENT_FORM_NAME,
  EMAIL_PLACEHOLDER,
  PHONE_PLACEHOLDER,
} from '../../../constants/payment';

const selectFieldInit = (number, pageName) => {
  const arr = [];
  for (let i = 0; i < number; i++) {
    arr.push({
      label: `${i + 1}`,
      value: i + 1,
      id: `page.${pageName}.component.recommend-help.day-select-${i + 1}-item`,
    });
  }
  return arr;
};

const installmentsInit = (currency, validRates, pageName) => {
  const result = [];

  for (let i = 0; i < validRates.length; i++) {
    result.push({
      label: `${
        currency
          ? getBalanceWithCurrency(validRates[i], currency)
          : validRates[i]
      }`,
      value: validRates[i],
      id: `page.${pageName}.component.recommend-help.rate-select-${validRates[i]}-item`,
    });
  }

  return result;
};

const calculateFirstPaymentDate = (day) => {
  const today = dayjs();
  let thisMonthOnDay = today.clone().date(day);

  let nextMonth = false;
  while (thisMonthOnDay.diff(today, 'days') < 5) {
    thisMonthOnDay = thisMonthOnDay.add(1, 'months');
    nextMonth = true;
  }

  return {
    value: thisMonthOnDay,
    nextMonth,
  };
};

const ibanFormatter = (iban) => {
  if (!iban) return '';
  return IBAN.printFormat(iban, ['-']);
};

const ibanCheck = (iban) => iban && IBAN.isValid(iban);
const nameCheck = (name) => !!(name && name.trim());

const ibanValidator = (iban) => (ibanCheck(iban) ? undefined : 'Invalid IBAN');
const nameValidator = (name = '') => (nameCheck(name) ? undefined : 'Required');
const agreeValidator = (agree) => (agree ? undefined : 'Must be agreed');
const mobilePhoneCheck = (phone) => {
  if (!phone) {
    return undefined;
  }
  return mobileValidator()(phone);
};

class RecommendHelpCalculator extends React.Component {
  constructor(props) {
    super(props);
    const { value, nextMonth } = calculateFirstPaymentDate(props.values.day);
    this.state = {
      firstInstallmentDate: value,
      nextMonth,
    };
  }

  onDayChange = (event, value) => {
    const { value: calculatedValue, nextMonth } = calculateFirstPaymentDate(
      value
    );
    this.setState({
      firstInstallmentDate: calculatedValue,
      nextMonth,
    });
  };

  render() {
    const {
      currency,
      styles,
      handleSubmit,
      onSubmit,
      validRates,
      className,
      values,
      invalid,
      pageName,
    } = this.props;

    const { nextMonth, firstInstallmentDate } = this.state;

    //const isValidName = nameCheck(accountHolder); name validation

    const selectFieldOptions = selectFieldInit(31, pageName);
    const installmentsOptions = installmentsInit(
      currency,
      validRates,
      pageName
    );

    return (
      <div
        className={
          className || styles['recommend-help-calculator__form-wrapper']
        }
      >
        <Form
          className={styles['recommend-help-calculator__form']}
          onSubmit={handleSubmit(({ mobilePhone, ...values }) =>
            onSubmit({
              ...values,
              mobilePhone:
                (mobilePhone && phoneTransformer(mobilePhone)[0]) || null,
              firstInstallmentDate: [
                firstInstallmentDate.year(),
                firstInstallmentDate.month() + 1,
                firstInstallmentDate.date(),
              ],
            })
          )}
        >
          <div className={styles['recommend-help-calculator__row']}>
            <Translation className={styles['recommend-help-calculator__label']}>
              SEPA_REQUEST_PANEL_RATE
            </Translation>
            <div
              className={styles['recommend-help-calculator__select-wrapper']}
            >
              <Select
                className={styles['recommend-help-calculator__select']}
                classNameInput={
                  styles['recommend-help-calculator__select-input']
                }
                classNameRoot={styles['recommend-help-calculator__select-root']}
                name="rate"
                options={installmentsOptions}
              />
              <Translation
                className={styles['recommend-help-calculator__past-label']}
              >
                SEPA_REQUEST_PANEL_MONTH
              </Translation>
            </div>
          </div>

          <div className={styles['recommend-help-calculator__row']}>
            <Translation className={styles['recommend-help-calculator__label']}>
              SEPA_DEBIT_PAYMENT_PANEL_IBAN_TEXT
            </Translation>
            <div
              className={
                styles['recommend-help-calculator__input-error-wrapper']
              }
            >
              <TextField
                name="iban"
                placeholder="DE00 0000 0000 0000 0000 00"
                className={styles['recommend-help-calculator__input-wrapper']}
                classNameInput={
                  styles[
                    !values.iban || ibanCheck(values.iban)
                      ? 'recommend-help-calculator__input-field'
                      : 'recommend-help-calculator__input-field-red'
                  ]
                }
                classNameRoot={styles['recommend-help-calculator__input-root']}
                onFormat={ibanFormatter}
                validate={ibanValidator}
              />
              {values.iban && !ibanCheck(values.iban) && (
                <div
                  className={styles['recommend-help-calculator__input-error']}
                >
                  Die eingegebene IBAN ist nicht gültig
                </div>
              )}
            </div>
          </div>

          <div className={styles['recommend-help-calculator__row']}>
            <Translation className={styles['recommend-help-calculator__label']}>
              SEPA_DEBIT_PAYMENT_PANEL_OWNER
            </Translation>
            <TextField
              name="accountHolder"
              className={styles['recommend-help-calculator__input-wrapper']}
              classNameInput={
                styles[
                  nameCheck(values.accountHolder)
                    ? 'recommend-help-calculator__input-field'
                    : 'recommend-help-calculator__input-field-red'
                ]
              }
              classNameRoot={styles['recommend-help-calculator__input-root']}
              validate={nameValidator}
            />
          </div>

          <div className={styles['recommend-help-calculator__row']}>
            <Translation className={styles['recommend-help-calculator__label']}>
              SEPA_DEBIT_PAYMENT_PANEL_MOVE_IN_DATE
            </Translation>
            <div
              className={styles['recommend-help-calculator__select-wrapper']}
            >
              <Select
                className={styles['recommend-help-calculator__select']}
                classNameInput={
                  styles['recommend-help-calculator__select-input']
                }
                classNameRoot={styles['recommend-help-calculator__select-root']}
                name="day"
                options={selectFieldOptions}
                onChange={this.onDayChange}
              />
              <Translation
                className={styles['recommend-help-calculator__past-label']}
              >
                SEPA_REQUEST__PANEL_IN_MONTH
              </Translation>
            </div>
          </div>

          <div className={styles['recommend-help-calculator__row']}>
            <Translation className={styles['recommend-help-calculator__label']}>
              SEPA_REQUEST_PANEL_DEBIT_ON_KEY
            </Translation>
            <div
              className={
                styles['recommend-help-calculator__input-with-description']
              }
            >
              <TextField
                className={styles['recommend-help-calculator__input-wrapper']}
                classNameInput={
                  styles['recommend-help-calculator__input-field']
                }
                classNameRoot={styles['recommend-help-calculator__input-root']}
                value={
                  firstInstallmentDate &&
                  firstInstallmentDate.format('DD.MM.YYYY')
                }
                readonly
              />
              {nextMonth && (
                <Translation
                  className={
                    styles['recommend-help-calculator__input-description']
                  }
                >
                  SEPA_REQUEST_PANEL_DEBIT_ON_TEXT
                </Translation>
              )}
            </div>
          </div>

          <div>
            <Paper className={styles['recommend-help-calculator__text-area']}>
              <Translation isHtml={false}>SEPA_MANDATE_LEGAL</Translation>
            </Paper>
          </div>

          <div className={styles['recommend-help-calculator__checkbox']}>
            <div
              className={
                styles['recommend-help-calculator__checkbox-description']
              }
            >
              <Translation>SEPA_MANDATE_CHECKBOX</Translation>
            </div>
            <Checkbox
              className={styles['recommend-help-calculator__checkbox-input']}
              name="agree"
              id={`page.${pageName}.component.recommend-help.checkbox`}
              validate={agreeValidator}
            />
          </div>
          <div className={styles['recommend-help-calculator__row']}>
            <Translation className={styles['recommend-help-calculator__label']}>
              SEPA_REQUEST_PANEL_DEBIT_EMAIL_KEY
            </Translation>
            <div
              className={
                styles['recommend-help-calculator__input-with-description']
              }
            >
              <TextField
                name="email"
                placeholder={EMAIL_PLACEHOLDER}
                className={styles['recommend-help-calculator__input-wrapper']}
                classNameInput={
                  styles[
                    !values.email || !emailValidator(values.email)
                      ? 'recommend-help-calculator__input-field'
                      : 'recommend-help-calculator__input-field-red'
                  ]
                }
                classNameRoot={styles['recommend-help-calculator__input-root']}
                validate={emailValidator}
              />
              <Translation
                className={
                  styles['recommend-help-calculator__input-description']
                }
              >
                SEPA_REQUEST_PANEL_DEBIT_EMAIL_TEXT
              </Translation>
            </div>
          </div>

          <div className={styles['recommend-help-calculator__row']}>
            <Translation className={styles['recommend-help-calculator__label']}>
              SEPA_REQUEST_PANEL_PHONE_KEY
            </Translation>
            <div
              className={
                styles['recommend-help-calculator__input-with-description']
              }
            >
              <TextField
                name="mobilePhone"
                placeholder={PHONE_PLACEHOLDER}
                className={styles['recommend-help-calculator__input-wrapper']}
                classNameInput={
                  styles[
                    !mobilePhoneCheck(values.mobilePhone)
                      ? 'recommend-help-calculator__input-field'
                      : 'recommend-help-calculator__input-field-red'
                  ]
                }
                classNameRoot={styles['recommend-help-calculator__input-root']}
                validate={mobilePhoneCheck}
              />
              <Translation
                className={
                  styles['recommend-help-calculator__input-description']
                }
              >
                SEPA_REQUEST_PANEL_PHONE_TEXT
              </Translation>
            </div>
          </div>

          <Button
            id={`page.${pageName}.component.calculator.button`}
            className={styles['recommend-help-calculator__button']}
            type="submit"
            disabled={invalid}
          >
            <Translation>SEPA_REQUEST_PANEL_BUTTON_TEXT</Translation>
          </Button>
        </Form>

        <div className={styles['recommend-help-calculator__remark-wrapper']}>
          <div className={styles['recommend-help-calculator__remark']}>
            <Translation>CALCULATOR_FINEPRINT</Translation>
          </div>
        </div>
      </div>
    );
  }
}

RecommendHelpCalculator.propTypes = {
  onSelectChange: PropTypes.func,
  picture: PropTypes.string,
  form: PropTypes.any,
  onSubmit: PropTypes.func,
};

const connected = connectTo(
  (state) => ({
    values: getFormValues(INSTALLMENT_FORM_NAME)(state),
  }),
  {},
  RecommendHelpCalculator
);

export default reduxForm({
  form: INSTALLMENT_FORM_NAME,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(connected);
